import apSupport from '../data/ap-support.json';
import productServiceMapping from '../data/product-service-mapping.json';
import { getReverseServiceAdjacency } from './dependencyAdapter';

/**
 * Schema for `ap-support.json`. A/P (Active/Passive) availability is fully
 * data-driven by this file — both the version gate and the per-platform
 * exclusions live in JSON.
 *
 * The exclusion list is **service-rooted**: within a version entry,
 * `unsupportedServices` maps a platform bucket (`eks` / `aks` / `openshift` /
 * `linux`) to a list of graph SERVICE names that cannot run on the secondary
 * site. The set of excluded PRODUCTS is derived from these services by
 * `deriveSecondaryPlan` (consumer cascade + all-or-nothing owner rule) — the
 * JSON never names a product key directly.
 *
 * Presence of a bucket key (even with `[]`) means A/P is available for that
 * platform on that version. Absence of the bucket key means A/P is
 * unavailable for that platform on that version.
 */
interface ApSupportSchema {
  sourceDoc?: string;
  versions: {
    [version: string]: {
      unsupportedServices: { [platform: string]: string[] | undefined };
    };
  };
}

const data = apSupport as ApSupportSchema;

interface ProductDef {
  type: string;
  partialSelection: boolean;
  services: { name: string }[];
}
const products = (productServiceMapping as any).products as Record<string, ProductDef>;

/**
 * Maps a calculator deploymentType to the platform bucket key used by
 * `ap-support.json`. Returns null only for unknown deploymentType inputs.
 */
function getPlatformBucket(deploymentType: string): string | null {
  if (!deploymentType) return null;
  if (deploymentType === 'air-eks') return 'eks';
  if (deploymentType === 'air-aks') return 'aks';
  if (deploymentType === 'air-openshift') return 'openshift';
  if (['ha', 'lite', 'eval'].includes(deploymentType)) return 'linux';
  return null;
}

/**
 * Raw list of banned service names for a deployment + version. Empty when the
 * bucket/version isn't configured (→ A/P has no exclusions there).
 */
function getBannedServices(deploymentType: string, version: string): string[] {
  const bucket = getPlatformBucket(deploymentType);
  if (!bucket) return [];
  const versionCfg = data.versions[version];
  if (!versionCfg) return [];
  return versionCfg.unsupportedServices[bucket] || [];
}

/**
 * Pure core of the derivation — kept separate from data/graph lookups so it
 * can be unit-tested with synthetic graphs. Two-phase fixpoint:
 *
 *   Phase 1 (service-ban closure): starting from the seed banned services,
 *     mark every service that DEPENDS ON a banned service (walk reverse edges)
 *     as banned too, to a fixpoint.
 *   Phase 2 (product exclusion): for each product P, if any of P's OWNED
 *     services is banned:
 *       - partialSelection:false (all-or-nothing) → exclude P and add ALL of
 *         P's owned services to the ban set (feeds back into Phase 1).
 *       - partialSelection:true (e.g. platform) → keep P as long as it has any
 *         surviving (non-banned) owned service; its banned services are dropped
 *         from the secondary resolution by stripServices. Exclude P only when
 *         EVERY owned service is banned (nothing left to keep) — this never
 *         fires for platform today but handles future user-selectable
 *         partialSelection products.
 *   Loop the two phases until neither the ban set nor the excluded set grows.
 *
 * The exclusion predicate is `ownedServices ∩ banned`, NOT a forward
 * dependency closure — Phase 1 already propagates bans to the owned service of
 * every (transitive) consumer, and an owned-set check correctly catches an
 * all-or-nothing product that owns a banned service it does not itself depend
 * on.
 */
function computeExcludedProducts(
  seedBannedServices: string[],
  reverseAdj: Record<string, string[]>,
  productDefs: Record<string, ProductDef>,
): { excludedProductKeys: Set<string>; bannedServices: Set<string> } {
  const excluded = new Set<string>();
  if (seedBannedServices.length === 0) {
    return { excludedProductKeys: excluded, bannedServices: new Set<string>() };
  }

  const banned = new Set<string>(seedBannedServices);

  const closeServiceBan = () => {
    const queue = Array.from(banned);
    while (queue.length > 0) {
      const svc = queue.shift()!;
      for (const dependent of reverseAdj[svc] || []) {
        if (!banned.has(dependent)) {
          banned.add(dependent);
          queue.push(dependent);
        }
      }
    }
  };

  let changed = true;
  while (changed) {
    changed = false;
    closeServiceBan();
    for (const key of Object.keys(productDefs)) {
      if (excluded.has(key)) continue;
      const def = productDefs[key];
      if (!def || def.type === 'fabric') continue;
      const owned = def.services.map((s) => s.name);
      if (!owned.some((s) => banned.has(s))) continue;
      // partialSelection owners (e.g. platform) are the shared backbone: keep
      // them as long as ANY owned service survives — stripServices drops the
      // banned ones from the secondary resolution. Exclude only when EVERY
      // owned service is banned; then there is nothing left to keep and leaving
      // the product in the set would render it as a zero-footprint phantom on
      // the passive site with no "unsupported" callout. Never hits platform
      // today (it always retains sfcore/gateway/base) — this guards future
      // user-selectable partialSelection products.
      if (def.partialSelection && !owned.every((s) => banned.has(s))) continue;
      excluded.add(key);
      owned.forEach((s) => banned.add(s));
      changed = true;
    }
  }

  // Return the CLOSED ban set (seed + reverse-dependency closure + owned
  // services of all-or-nothing excluded products), not just the seed: the
  // secondary pass strips exactly these services after resolution, and the
  // closure guarantees no surviving service has a dependency edge into a
  // stripped one.
  return { excludedProductKeys: excluded, bannedServices: banned };
}

/**
 * Derives the secondary (passive) site plan for the given deployment + version
 * from the service-rooted ban list. Returns:
 *   - `excludedProductKeys`: products dropped from the secondary product set
 *     (consumer cascade + all-or-nothing owner rule).
 *   - `bannedServices`: the CLOSED ban set — stripped from the secondary
 *     resolution AFTER `resolveServices` (via `stripServices`) so a banned
 *     service never contributes to passive sizing even when its
 *     `partialSelection` owner (e.g. `platform` owning `taas`) is force-included
 *     via an explicit key. This closes the former legacy explicit-`platform`
 *     URL leak: product filtering alone cannot express "keep the product, drop
 *     one service" — the post-resolution strip can.
 */
export function deriveSecondaryPlan(
  deploymentType: string,
  version: string,
  distribution: string,
): { excludedProductKeys: Set<string>; bannedServices: Set<string> } {
  const banned = getBannedServices(deploymentType, version);
  if (banned.length === 0) {
    return {
      excludedProductKeys: new Set<string>(),
      bannedServices: new Set<string>(),
    };
  }
  const reverseAdj = getReverseServiceAdjacency(distribution);
  return computeExcludedProducts(banned, reverseAdj, products);
}

/**
 * Returns true when A/P (Active/Passive) is available for the given
 * deployment + version. Availability is presence-driven: the version must be
 * configured and the platform bucket must be present within it.
 */
export function isApAvailable(deploymentType: string, version: string): boolean {
  if (!deploymentType || !version) return false;
  const bucket = getPlatformBucket(deploymentType);
  if (!bucket) return false;
  const versionCfg = data.versions[version];
  if (!versionCfg) return false;
  return bucket in versionCfg.unsupportedServices;
}

export const __testing = {
  getPlatformBucket,
  getBannedServices,
  computeExcludedProducts,
};
